import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AuthCore } from './core';
import type { AuthState, GuestSessionMetadata, SessionInfo } from './types';

export interface AuthContextValue {
  /** null = bootstrapping; otherwise terminal state. */
  authState: AuthState;
  setAuthState: (state: AuthState) => void;
  isAuthenticated: boolean;
  isGuest: boolean;
  /** Last fetched session info — handy for guest-aware UI. Refreshed on auth events. */
  sessionInfo: SessionInfo | null;
}

const AuthContext = createContext<AuthContextValue>({
  authState: null,
  setAuthState: () => {},
  isAuthenticated: false,
  isGuest: false,
  sessionInfo: null,
});

export interface AuthProviderProps {
  core: AuthCore;
  children: React.ReactNode;
  /** Bypass mount-time bootstrap (RN screenshot mode). */
  initialState?: AuthState;
}

export const AuthProvider = ({
  core,
  children,
  initialState = null,
}: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>(initialState);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);

  // Single mount-level bootstrap: read whatever the storage adapter has, decode
  // the JWT, set authenticated/guest/unauthenticated terminal state.
  useEffect(() => {
    if (initialState !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const info = await core.getSessionInfo();
        if (cancelled) return;
        setSessionInfo(info);
        if (info.token) {
          setAuthState(info.isGuest ? 'guest' : 'authenticated');
        } else {
          setAuthState('unauthenticated');
        }
      } catch (err) {
        console.error('[AuthProvider] bootstrap error', err);
        if (!cancelled) setAuthState('unauthenticated');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [core, initialState]);

  // Re-read sessionInfo when the auth core fires events (token refresh, guest
  // upgrade, signin/register/logout). Cheap — JWT decode only.
  useEffect(() => {
    return core.onAuthEvent(async event => {
      try {
        const info = await core.getSessionInfo();
        setSessionInfo(info);
        if (event === 'sessionCleared' || !info.token) {
          setAuthState('unauthenticated');
        } else {
          setAuthState(info.isGuest ? 'guest' : 'authenticated');
        }
      } catch {
        /* noop */
      }
    });
  }, [core]);

  const isAuthenticated =
    authState === 'authenticated' || authState === 'guest';
  const isGuest = authState === 'guest';

  const value = useMemo<AuthContextValue>(
    () => ({ authState, setAuthState, isAuthenticated, isGuest, sessionInfo }),
    [authState, isAuthenticated, isGuest, sessionInfo],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => useContext(AuthContext);

const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

export interface UseAuthReturn extends AuthContextValue {
  authError: string | null;
  login: (email: string, password: string) => Promise<unknown>;
  loginWithExternal: (tokens: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  }) => Promise<void>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    language?: string,
  ) => Promise<unknown>;
  createGuest: (metadata?: GuestSessionMetadata) => Promise<unknown>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  onRequestPasswordReset: (
    email: string,
    language?: string,
  ) => Promise<unknown>;
  onConfirmPasswordReset: (payload: {
    code: string;
    newPassword: string;
  }) => Promise<unknown>;
  getAuthToken: () => Promise<string | null>;
  getSessionInfo: () => Promise<SessionInfo>;
}

/** Build a `useAuth` hook bound to a specific AuthCore instance. */
export function makeUseAuth(core: AuthCore) {
  return function useAuth(): UseAuthReturn {
    const ctx = useContext(AuthContext);
    const { setAuthState } = ctx;
    const [authError, setAuthError] = useState<string | null>(null);

    const login = useCallback(
      async (email: string, password: string) => {
        try {
          const result = await core.signin({ emailAddress: email, password });
          const info = await core.getSessionInfo();
          setAuthState(info.isGuest ? 'guest' : 'authenticated');
          setAuthError(null);
          return result;
        } catch (err: unknown) {
          const e = err as { response?: { data?: { message?: string } } };
          setAuthError(e?.response?.data?.message || 'Login failed');
          throw err;
        }
      },
      [setAuthState],
    );

    const loginWithExternal = useCallback(
      async (tokens: {
        accessToken: string;
        refreshToken: string;
        expiresAt: number;
      }) => {
        await core.setSessionFromExternal(tokens);
        const info = await core.getSessionInfo();
        setAuthState(info.isGuest ? 'guest' : 'authenticated');
        setAuthError(null);
      },
      [setAuthState],
    );

    const register = useCallback(
      async (
        email: string,
        password: string,
        firstName: string,
        lastName: string,
        language: string = 'en-US',
      ) => {
        try {
          const result = await core.register({
            emailAddress: email,
            password,
            firstName,
            lastName,
            language,
          });
          const info = await core.getSessionInfo();
          setAuthState(info.isGuest ? 'guest' : 'authenticated');
          setAuthError(null);
          return result;
        } catch (err: unknown) {
          const e = err as { response?: { data?: { message?: string } } };
          setAuthError(e?.response?.data?.message || 'Registration failed');
          throw err;
        }
      },
      [setAuthState],
    );

    const createGuest = useCallback(
      async (metadata?: GuestSessionMetadata) => {
        try {
          const result = await core.createGuestSession(metadata);
          setAuthState('guest');
          setAuthError(null);
          return result;
        } catch (err: unknown) {
          const e = err as { response?: { data?: { message?: string } } };
          setAuthError(
            e?.response?.data?.message || 'Failed to create guest session',
          );
          throw err;
        }
      },
      [setAuthState],
    );

    const logout = useCallback(async () => {
      await core.logout();
      setAuthState('unauthenticated');
      setAuthError(null);
    }, [setAuthState]);

    const deleteAccount = useCallback(async () => {
      await core.deleteAccount();
      setAuthState('unauthenticated');
      setAuthError(null);
    }, [setAuthState]);

    // Silently refresh the token on an interval while authed, so the next
    // user action doesn't hit the http client's 401 retry path.
    useEffect(() => {
      if (ctx.authState === 'unauthenticated' || ctx.authState === null) return;
      const id = setInterval(() => {
        core.getAuthToken().catch(() => {});
      }, REFRESH_INTERVAL_MS);
      return () => clearInterval(id);
    }, [ctx.authState]);

    return {
      ...ctx,
      authError,
      login,
      loginWithExternal,
      register,
      createGuest,
      logout,
      deleteAccount,
      onRequestPasswordReset: (email: string, language?: string) =>
        core.requestPasswordReset(email, language),
      onConfirmPasswordReset: (payload: {
        code: string;
        newPassword: string;
      }) => core.confirmPasswordReset(payload),
      getAuthToken: () => core.getAuthToken(),
      getSessionInfo: () => core.getSessionInfo(),
    };
  };
}
